import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Send, CalendarDays, Loader2, Bot, User } from 'lucide-react';
import { toast } from 'sonner';

export default function AIChat() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [histRes, qRes] = await Promise.all([
          api.get(`/chat/history/${quoteId}`),
          api.get(`/quotes/${quoteId}`)
        ]);
        setMessages(histRes.data);
        setQuote(qRes.data);
      } catch {
        toast.error('Errore nel caricamento');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [quoteId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setSending(true);

    // Optimistic update
    const tempMsg = { role: 'user', content: text, message_id: 'temp', created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, tempMsg]);

    try {
      const res = await api.post('/chat/message', { quote_id: quoteId, message: text });
      setMessages((prev) => [
        ...prev.filter((m) => m.message_id !== 'temp'),
        { role: 'user', content: text, message_id: `u_${Date.now()}`, created_at: new Date().toISOString() },
        { role: 'assistant', content: res.data.message, message_id: `a_${Date.now()}`, created_at: new Date().toISOString() }
      ]);
      if (res.data.quote) setQuote(res.data.quote);
    } catch {
      toast.error('Errore nell\'invio del messaggio');
      setMessages((prev) => prev.filter((m) => m.message_id !== 'temp'));
      setInput(text);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleProceed = async () => {
    try {
      await api.put(`/quotes/${quoteId}/complete-chat`);
      toast.success('Chat completata!');
      navigate(`/calendario/${quoteId}`);
    } catch {
      toast.error('Errore');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-stone-50">
        <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-stone-50" data-testid="ai-chat-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 flex-shrink-0">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="chat-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3 flex-1">
            <div className="w-9 h-9 rounded-full bg-slate-900 flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-900">Operatore REGOLA</p>
              <p className="text-xs text-emerald-500 font-medium">Online</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full text-xs"
            onClick={handleProceed}
            data-testid="proceed-booking-btn"
          >
            <CalendarDays className="w-3.5 h-3.5 mr-1.5" /> Prenota
          </Button>
        </div>
      </header>

      {/* Price Summary Bar */}
      {quote && (
        <div className="bg-slate-900 text-white px-4 py-2.5 flex-shrink-0">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <span className="text-xs text-slate-400">Prezzo stimato attuale</span>
            <span className="font-bold text-sm" data-testid="chat-price">EUR {quote.price_min} - {quote.price_max}</span>
          </div>
        </div>
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 overflow-y-auto" ref={scrollRef}>
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
          {/* Welcome message if no messages */}
          {messages.length === 0 && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 text-slate-600" />
              </div>
              <div className="chat-bubble-ai px-4 py-3 max-w-[80%]">
                <p className="text-sm">
                  Benvenuto! Sono l'operatore virtuale di REGOLA.
                  Ho gia' visionato il tuo preventivo. Hai altre problematiche da segnalare o altri serramenti da aggiungere?
                  Se sei soddisfatto, puoi procedere direttamente alla prenotazione.
                </p>
              </div>
            </div>
          )}

          {messages.map((msg) => (
            <div key={msg.message_id} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
              {msg.role === 'assistant' && (
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-slate-600" />
                </div>
              )}
              <div className={`${msg.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-ai'} px-4 py-3 max-w-[80%]`}>
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
              {msg.role === 'user' && (
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
          ))}

          {sending && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 text-slate-600" />
              </div>
              <div className="chat-bubble-ai px-4 py-3">
                <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="bg-white border-t border-slate-200 flex-shrink-0">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <form
            onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
            className="flex gap-2"
          >
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Scrivi un messaggio..."
              className="rounded-full"
              disabled={sending}
              data-testid="chat-input"
            />
            <Button
              type="submit"
              size="icon"
              className="rounded-full bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0"
              disabled={sending || !input.trim()}
              data-testid="chat-send-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
